import { onIdTokenChanged, signInWithCustomToken, type User } from "firebase/auth";
import { createContext, type ReactNode, useEffect, useMemo, useState } from "react";
import { clearPreferredMerchantId, setActiveMerchantId } from "@/lib/api";
import { goToLogin, isSignOutReason, subscribeAuthBroadcast } from "@/lib/auth-utils";
import { auth } from "@/lib/firebase";
import { queryClient } from "@/lib/query-client";
import { resetSessionCache } from "@/lib/session";

interface FirebaseAuthContext {
  /** True once Firebase has restored persisted auth state at least once. */
  isReady: boolean;
  /** The signed-in Firebase user, or null. */
  user: User | null;
  /** Derived: `isReady && (user !== null || testMode)`. */
  isAuthenticated: boolean;
  /** True only when Playwright has injected `window.__E2E_TOKEN` in dev. */
  testMode: boolean;
}

export const FirebaseAuthCtx = createContext<FirebaseAuthContext>({
  isReady: false,
  user: null,
  isAuthenticated: false,
  testMode: false,
});

export function FirebaseAuthSync({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [testMode, setTestMode] = useState(false);

  useEffect(() => {
    // onIdTokenChanged rather than onAuthStateChanged so the context also
    // reflects token refreshes, not only sign-in and sign-out.
    let lastUid: string | null = auth.currentUser?.uid ?? null;
    const unsubscribe = onIdTokenChanged(auth, (firebaseUser) => {
      const hasTestToken = import.meta.env.DEV && !!window.__E2E_TOKEN;
      const nextUid = firebaseUser?.uid ?? null;
      if (nextUid !== lastUid) {
        // Account switch (or sign-out): drop per-session identity so a cached
        // merchant pointer / /auth/me from the prior account can't bleed in.
        lastUid = nextUid;
        resetSessionCache();
        queryClient.clear();
        setActiveMerchantId(null);
        clearPreferredMerchantId();
      }
      setTestMode(hasTestToken && !firebaseUser);
      setUser(firebaseUser);
      setIsReady(true);
    });

    if (import.meta.env.DEV) {
      window.__E2E_SIGN_IN = (customToken: string) => signInWithCustomToken(auth, customToken);
    }

    return unsubscribe;
  }, []);

  // Cross-tab sign-out: when another tab signs out (user-initiated or kicked),
  // hard-redirect this tab too instead of leaving it on a stale shell that
  // will only fail on the next request.
  useEffect(() => {
    return subscribeAuthBroadcast(({ reason }) => {
      if (typeof window === "undefined") return;
      if (window.location.pathname.startsWith("/login")) return;
      // Same-origin XSS could post a forged reason; coerce to the known set
      // before reflecting it into the URL.
      goToLogin(isSignOutReason(reason) ? reason : "remote-tab");
    });
  }, []);

  // No refresh timers here: every request reads its token through
  // `getIdToken()`, which the SDK renews on demand once it is within 30s of
  // expiry — including the first request after a laptop wakes.

  const value = useMemo<FirebaseAuthContext>(
    () => ({
      isReady,
      user,
      isAuthenticated: isReady && (user !== null || testMode),
      testMode,
    }),
    [isReady, user, testMode],
  );

  return <FirebaseAuthCtx.Provider value={value}>{children}</FirebaseAuthCtx.Provider>;
}
